/*
 * KSA · Knitwear & Sweater Alterations — shared behaviour
 * Tape-measure rulers, scroll reveal, Find Your Fix hotspots, Knit Builder estimator.
 * Loaded (deferred) by every ksa-*.liquid section; guarded so it only initialises once.
 * Re-initialises sections on shopify:section:load for the theme editor.
 */
(function () {
  'use strict';
  if (window.KSAKnit) return;
  var K = (window.KSAKnit = {});

  function qa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }
  function scope(root, sel) {
    var list = qa(root, sel);
    if (root.matches && root.matches(sel)) list.unshift(root);
    return list;
  }
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------------- Tape-measure ruler ---------------- */
  function drawRuler(c) {
    var w = c.clientWidth;
    var h = c.clientHeight;
    if (!w || !h) return;
    var dpr = window.devicePixelRatio || 1;
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
    var x = c.getContext('2d');
    if (!x) return;
    x.setTransform(dpr, 0, 0, dpr, 0, 0);
    var d = c.dataset;
    var gap = Math.max(2, parseFloat(d.gap) || 8);
    var every = parseInt(d.every, 10) || 10;
    var half = every % 2 === 0 ? every / 2 : 0;
    var ink = d.ink || '#1B1B18';
    var mute = d.mute || '#8A8578';
    var accent = d.accent || '#B23B2E';
    x.clearRect(0, 0, w, h);
    if (d.bg) {
      x.fillStyle = d.bg;
      x.fillRect(0, 0, w, h);
    }
    var fam = '';
    try {
      fam = getComputedStyle(c).getPropertyValue('--ksa-fm').trim();
    } catch (e) {}
    x.font = '9px ' + (fam || 'monospace');
    x.textBaseline = 'top';
    for (var i = 0, n = 0; i <= w; i += gap, n++) {
      var big = n % every === 0;
      var mid = half && n % half === 0;
      x.strokeStyle = big ? ink : mute;
      x.lineWidth = big ? 1.2 : 1;
      var len = big ? h * 0.55 : mid ? h * 0.38 : h * 0.22;
      var px = Math.round(i) + 0.5;
      x.beginPath();
      x.moveTo(px, 0);
      x.lineTo(px, len);
      x.stroke();
      if (big && n > 0) {
        var label = n / every;
        x.fillStyle = label % 5 === 0 ? accent : ink;
        x.fillText(String(label), i + 3, h * 0.5);
      }
    }
  }
  K.drawRulers = function (root) {
    qa(root || document, '[data-ksa-ruler]').forEach(drawRuler);
  };
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      K.drawRulers(document);
    }, 120);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      K.drawRulers(document);
    });
  }

  /* ---------------- Scroll reveal ---------------- */
  var io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              if (e.isIntersecting) {
                e.target.classList.add('is-in');
                io.unobserve(e.target);
              }
            });
          },
          { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
        )
      : null;
  K.reveal = function (root) {
    if (designMode) return; // keep everything visible while editing
    scope(root, '[data-ksa-animate]').forEach(function (sec) {
      if (sec.__ksaReveal) return;
      sec.__ksaReveal = true;
      sec.classList.add('ksa-anim');
      qa(sec, '.ksa-reveal').forEach(function (el) {
        if (io) io.observe(el);
        else el.classList.add('is-in');
      });
    });
  };

  /* ---------------- Find your fix ---------------- */
  K.fix = function (root) {
    scope(root, '[data-ksa-fix]').forEach(function (sec) {
      if (sec.__ksaFix) return;
      sec.__ksaFix = true;
      var panel = sec.querySelector('[data-ksa-panel]');
      var keys = ['num', 'title', 'desc', 'how', 'time', 'price'];
      function set(i) {
        var hot = sec.querySelector('.ksa-fix__hot[data-ksa-point="' + i + '"]');
        if (!hot || !panel) return;
        keys.forEach(function (k) {
          var el = panel.querySelector('[data-k="' + k + '"]');
          if (!el) return;
          var v = hot.getAttribute('data-' + k) || '';
          el.textContent = v;
          var cell = el.closest('[data-cell]');
          if (cell) cell.hidden = !v;
        });
        qa(sec, '[data-ksa-point]').forEach(function (b) {
          var on = b.getAttribute('data-ksa-point') === String(i);
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      sec.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-ksa-point]');
        if (b && sec.contains(b)) set(b.getAttribute('data-ksa-point'));
      });
      sec.__ksaSet = set;
    });
  };

  /* ---------------- Knit builder ---------------- */
  K.builder = function (root) {
    scope(root, '[data-ksa-builder]').forEach(function (sec) {
      if (sec.__ksaBuilder) return;
      sec.__ksaBuilder = true;
      var cfg = sec.dataset;
      var currency = cfg.currency || '$';
      var roundTo = parseFloat(cfg.round) || 1;
      var spread = parseInt(cfg.spread, 10) || 0;
      var suffix = cfg.suffix || '';
      var priceEl = sec.querySelector('[data-ksa-price]');
      var leadEl = sec.querySelector('[data-ksa-lead]');
      var listEl = sec.querySelector('[data-ksa-summary]');
      var swEl = sec.querySelector('[data-ksa-swname]');
      var cfgInput = sec.querySelector('[data-ksa-config]');
      var estInput = sec.querySelector('[data-ksa-estimate]');
      var groups = qa(sec, '[data-ksa-group]');

      groups.forEach(function (g) {
        if (!g.querySelector('input:checked')) {
          var first = g.querySelector('input');
          if (first) first.checked = true;
        }
      });

      function checked(group) {
        return sec.querySelector('[data-ksa-group="' + group + '"] input:checked');
      }
      function num(group, attr, fallback) {
        var i = checked(group);
        if (!i) return fallback;
        var v = parseFloat(i.getAttribute(attr));
        return isNaN(v) ? fallback : v;
      }

      function update() {
        var base = num('garment', 'data-price', 0);
        var yarn = num('yarn', 'data-pct', 100) / 100;
        var weight = num('weight', 'data-pct', 100) / 100;
        var add = num('stitch', 'data-price', 0);
        var weeks = num('weight', 'data-weeks', 0) + num('stitch', 'data-weeks', 0);
        var price = Math.round((base * yarn * weight + add) / roundTo) * roundTo;
        var priceText = currency + price.toLocaleString();
        if (priceEl) priceEl.textContent = priceText;

        var leadText = '';
        if (weeks > 0) leadText = (spread > 0 ? weeks + '–' + (weeks + spread) : String(weeks)) + (suffix ? ' ' + suffix : '');
        if (leadEl) {
          leadEl.textContent = leadText;
          leadEl.hidden = !leadText;
        }

        var parts = [];
        if (listEl) listEl.textContent = '';
        groups.forEach(function (g) {
          var i = g.querySelector('input:checked');
          if (!i) return;
          var legend = g.getAttribute('data-legend') || '';
          var label = i.getAttribute('data-label') || '';
          parts.push(legend + ': ' + label);
          if (listEl) {
            var li = document.createElement('li');
            var a = document.createElement('span');
            var b = document.createElement('span');
            a.textContent = legend;
            b.textContent = label;
            li.appendChild(a);
            li.appendChild(b);
            listEl.appendChild(li);
          }
          if (swEl && g.getAttribute('data-ksa-group') === 'colour') swEl.textContent = label;
        });

        if (cfgInput) cfgInput.value = parts.join(' | ');
        if (estInput) estInput.value = priceText + (leadText ? ' (' + leadText + ')' : '');
      }

      sec.addEventListener('change', function (e) {
        if (e.target.closest && e.target.closest('[data-ksa-group]')) update();
      });
      update();
    });
  };

  /* ---------------- Init ---------------- */
  K.init = function (root) {
    root = root || document;
    K.reveal(root);
    K.fix(root);
    K.builder(root);
    K.drawRulers(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      K.init(document);
    });
  } else {
    K.init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    K.init(e.target);
  });

  document.addEventListener('shopify:block:select', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var fix = t.closest('[data-ksa-fix]');
    if (fix && fix.__ksaSet && t.hasAttribute('data-ksa-point')) fix.__ksaSet(t.getAttribute('data-ksa-point'));
    if (t.tagName === 'DETAILS') t.open = true;
    var opt = t.querySelector && t.querySelector('.ksa-opt input');
    if (opt) {
      opt.checked = true;
      opt.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
})();
